import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../Layouts/Header';
import Footer from '../Layouts/Footer';
import bgImage from '../Assets/Images/ContactUsBg.jpg';
import { FaArrowDown } from "react-icons/fa";
import CircularText from '../Components/CircularText';
import toast, { Toaster } from 'react-hot-toast';
import { CONTACTUS } from '../API/Config';

const ContactUs = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.state?.scroll) {
      const section = document.getElementById('scrollDown');
      if (section) section.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location]);

  const handleScroll = () => {
    const section = document.getElementById('scrollDown');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const [formData, setFormData] = useState({
    fullname: '',
    email: '',
    phonenumber: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate phone number
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    if (!phoneRegex.test(formData.phonenumber)) {
      toast.error('Please enter a valid phone number.');
      setIsSubmitting(false);
      return;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address.');
      setIsSubmitting(false);
      return;
    }

    const toastId = toast.loading('Submitting request...');

    try {
      const response = await fetch(CONTACTUS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        toast.success('Request submitted successfully!', { id: toastId });
        setFormData({
          fullname: '',
          email: '',
          phonenumber: '',
          subject: '',
          message: '',
        });
      } else {
        toast.error(result.message || 'Failed to submit request.', { id: toastId });
      }
    } catch (err) {
      toast.error('Error submitting request. Please try again later.', { id: toastId });
      console.error('API Error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header />

      <div
        className="relative h-screen flex flex-col justify-between items-center text-white text-center px-4 py-12"
        style={{
          backgroundImage: `url(${bgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        }}
      >
        <div className="absolute inset-0 bg-black opacity-60 z-0"></div>

        <div className="relative z-10 flex flex-col items-center justify-center flex-grow space-y-6">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold">
            What’s on your mind?
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-200 font-light max-w-3xl">
            We’re here to help! Tell us what you’re looking for and we’ll get you connected to the right people.
          </p>
        </div>

        <div className="relative z-10 w-28 h-28 mb-8 flex items-center justify-center">
          <div onClick={handleScroll} className="absolute inset-0 flex items-center justify-center cursor-pointer">
            <CircularText text="VIVEKASOFTEK @ EXPLORE  " spinDuration={20} />
          </div>
          <button onClick={handleScroll} className="z-10 flex items-center justify-center">
            <FaArrowDown size={28} className="text-white" />
          </button>
        </div>
      </div>

      <div id="scrollDown" className="bg-white py-16 px-4 md:px-10">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 text-center">
            Request for Enquiry
          </h2>
          <p className="text-gray-600 text-center font-medium text-base md:text-lg mb-10">
            We’ve driven growth and purposeful transformation across every industry and we’re excited to build on your belief. Tell us a bit more about yourself, so we can get the ball rolling.
          </p>

          <form className="grid grid-cols-1 md:grid-cols-2 gap-6" onSubmit={handleSubmit}>
            <div className="flex flex-col">
              <label htmlFor="fullName" className="text-sm font-medium text-gray-700 mb-1">Full Name</label>
              <input
                type="text"
                id="fullName"
                name="fullname"
                value={formData.fullname}
                onChange={handleChange}
                className="border text-sm font-light border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div className="flex flex-col">
              <label htmlFor="email" className="text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="border text-sm font-light border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div className="flex flex-col">
              <label htmlFor="phone" className="text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              <input
                type="tel"
                id="phone"
                name="phonenumber"
                value={formData.phonenumber}
                onChange={handleChange}
                className="border text-sm font-light border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div className="flex flex-col">
              <label htmlFor="subject" className="text-sm font-medium text-gray-700 mb-1">Subject</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="border text-sm font-light border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex flex-col md:col-span-2">
              <label htmlFor="message" className="text-sm font-medium text-gray-700 mb-1">Your Message</label>
              <textarea
                id="message"
                name="message"
                rows="5"
                value={formData.message}
                onChange={handleChange}
                className="border text-sm font-light border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder="Write your message here..."
              ></textarea>
            </div>

            <div className="md:col-span-2 flex justify-end">
              <button
                type="submit"
                className="bg-black text-white font-semibold py-2 px-6 rounded transition flex items-center"
                disabled={isSubmitting}
              >
                {isSubmitting && (
                  <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="4" fill="none" />
                  </svg>
                )}
                {isSubmitting ? 'Submitting...' : 'Submit Enquiry'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <Footer />
      {/* <Toaster position="top-right" /> */}
    </>
  );
};

export default ContactUs;
